import React from 'react';
import {
  VictoryChart,
  VictoryBar,
  VictoryStack,
  VictoryLine,
  VictoryAxis,
  VictoryTheme,
  VictoryLegend,
  VictoryScatter,
  VictoryLabel,
  VictoryTooltip,
  VictoryVoronoiContainer,
} from 'victory';
import { formatPeriodES } from '../../../config/reportTranslations';
import './Charts.css';
import { REPORT_CHART_COLORS } from '../../../constants/colors';

/**
 * Gráfico 06: Composición del Patrimonio
 *
 * Stacked bar chart showing equity composition over time with leverage line:
 * - Capital social (bars)
 * - Reservas (bars)
 * - Superávit por valuaciones (bars)
 * - Resultados (bars)
 * - Apalancamiento line (secondary axis)
 *
 * Matches PDF report format from Atuntaqui_mar2025
 */

export interface Grafico06Data {
  period: string;              // YYYY-MM format
  capital_social: number;      // Capital social in USD (millions)
  reservas: number;            // Reservas in USD (millions)
  superavit_valuaciones: number; // Superávit por valuaciones in USD (millions)
  resultados: number;          // Resultados in USD (millions)
  apalancamiento: number;      // Leverage ratio (times)
}

export interface Grafico06Props {
  data: Grafico06Data[];
  title?: string;
}

const COLORS = REPORT_CHART_COLORS;

// SVG Pattern definitions for striped bars (matching PDF report style)
const StripedPatterns: React.FC = () => (
  <svg style={{ position: 'absolute', width: 0, height: 0 }}>
    <defs>
      {/* Dark gray with white diagonal stripes - rotate(-45) for opposite diagonal */}
      <pattern
        id="stripedPrimaryG06"
        patternUnits="userSpaceOnUse"
        width="4"
        height="4"
        patternTransform="rotate(-45)"
      >
        <rect width="4" height="4" fill={COLORS.primary} />
        <line x1="0" y1="0" x2="0" y2="4" stroke="white" strokeWidth="2" />
      </pattern>
      {/* Teal with white diagonal stripes - rotate(-45) for opposite diagonal */}
      <pattern
        id="stripedSecondaryG06"
        patternUnits="userSpaceOnUse"
        width="4"
        height="4"
        patternTransform="rotate(-45)"
      >
        <rect width="4" height="4" fill={COLORS.secondary} />
        <line x1="0" y1="0" x2="0" y2="4" stroke="white" strokeWidth="2" />
      </pattern>
    </defs>
  </svg>
);

export const Grafico06: React.FC<Grafico06Props> = ({
  data,
  title = 'Gráfico 06: Composición del Patrimonio'
}) => {
  if (!data || data.length === 0) {
    return (
      <div className="chart-container">
        <h4 className="chart-title">{title}</h4>
        <p className="chart-no-data">No hay datos disponibles para este período</p>
      </div>
    );
  }

  // Sort data by period (oldest first)
  const sortedData = [...data].sort((a, b) => a.period.localeCompare(b.period));

  // Convert to millions for display
  const toMillions = (value: number): number => value / 1_000_000;

  // Transform data for stacked bars (no labels - use tooltips on hover only)
  const capitalData = sortedData.map(d => ({
    x: formatPeriodES(d.period),
    y: toMillions(d.capital_social),
  }));

  const reservasData = sortedData.map(d => ({
    x: formatPeriodES(d.period),
    y: toMillions(d.reservas),
  }));

  const superavitData = sortedData.map(d => ({
    x: formatPeriodES(d.period),
    y: toMillions(d.superavit_valuaciones),
  }));

  const resultadosData = sortedData.map(d => ({
    x: formatPeriodES(d.period),
    y: toMillions(d.resultados),
  }));

  // Leverage line data - NO labels to avoid duplicates
  const leverageLineData = sortedData.map(d => ({
    x: formatPeriodES(d.period),
    y: d.apalancamiento,
  }));

  // Leverage scatter data with labels for display
  const leverageScatterData = sortedData.map(d => ({
    x: formatPeriodES(d.period),
    y: d.apalancamiento,
    label: d.apalancamiento.toFixed(1)
  }));

  // Calculate domains
  const maxStackValue = Math.max(...sortedData.map(d =>
    toMillions(d.capital_social + d.reservas + d.superavit_valuaciones + d.resultados)
  ));
  const maxLeverage = Math.max(...leverageLineData.map(d => d.y));

  // Left axis domain (Millones)
  const leftAxisMax = Math.ceil((maxStackValue * 1.1) / 20) * 20; // Round to nearest 20

  // Right axis domain (Veces - leverage typically 5-10)
  const rightAxisMax = 10; // Fixed 0-10 scale for Veces

  // Scale factor to map leverage values to millones axis
  const scaleFactor = leftAxisMax / rightAxisMax;

  return (
    <div className="chart-container">
      {/* SVG pattern definitions */}
      <StripedPatterns />

      <h4 className="chart-title">{title}</h4>

      <div className="chart-wrapper chart-dual-axis">
        {/* Main chart with stacked bars */}
        <VictoryChart
          theme={VictoryTheme.material}
          domainPadding={{ x: 30, y: 10 }}
          padding={{ top: 50, bottom: 60, left: 55, right: 55 }}
          height={300}
          containerComponent={
            <VictoryVoronoiContainer
              labels={({ datum }) => datum.label}
              labelComponent={<VictoryTooltip style={{ fontSize: 9 }} />}
            />
          }
        >
          {/* Legend */}
          <VictoryLegend
            x={20}
            y={5}
            orientation="horizontal"
            gutter={12}
            style={{ labels: { fontSize: 7 } }}
            data={[
              { name: 'Capital', symbol: { fill: COLORS.primary } },
              { name: 'Reservas', symbol: { fill: COLORS.secondary } },
              { name: 'Superávit', symbol: { fill: COLORS.primary, opacity: 0.5 } },
              { name: 'Resultados', symbol: { fill: COLORS.secondary, opacity: 0.5 } },
              { name: 'Apalancamiento', symbol: { fill: COLORS.primary, type: 'minus' } },
            ]}
          />

          {/* X-Axis */}
          <VictoryAxis
            style={{
              axis: { stroke: '#666' },
              tickLabels: {
                fontSize: 9,
                padding: 5,
                angle: -45,
                textAnchor: 'end'
              }
            }}
          />

          {/* Y-Axis Left (Millones USD) */}
          <VictoryAxis
            dependentAxis
            domain={[0, leftAxisMax]}
            tickFormat={(t: number) => `${t.toFixed(0)}`}
            label="Millones"
            style={{
              axis: { stroke: '#666' },
              axisLabel: { fontSize: 9, padding: 40 },
              tickLabels: { fontSize: 9, padding: 5 },
              grid: { stroke: '#e0e0e0', strokeDasharray: '3,3' }
            }}
          />

          {/* Y-Axis Right (Veces) - 0 to 10 scale */}
          <VictoryAxis
            dependentAxis
            orientation="right"
            domain={[0, leftAxisMax]}
            tickValues={[0, leftAxisMax * 0.2, leftAxisMax * 0.4, leftAxisMax * 0.6, leftAxisMax * 0.8, leftAxisMax]}
            tickFormat={(t: number) => `${(t / scaleFactor).toFixed(0)}`}
            label="Veces"
            style={{
              axis: { stroke: '#666' },
              axisLabel: { fontSize: 9, padding: 40 },
              tickLabels: { fontSize: 9, padding: 5 }
            }}
          />

          {/* Stacked Bars - 4 segments with solid and striped patterns */}
          <VictoryStack>
            {/* 1. Capital - solid primary (dark gray) */}
            <VictoryBar
              data={capitalData}
              barWidth={12}
              style={{ data: { fill: COLORS.primary } }}
            />
            {/* 2. Reservas - solid secondary (teal) */}
            <VictoryBar
              data={reservasData}
              barWidth={12}
              style={{ data: { fill: COLORS.secondary } }}
            />
            {/* 3. Superávit - striped primary (dark gray with white stripes) */}
            <VictoryBar
              data={superavitData}
              barWidth={12}
              style={{ data: { fill: 'url(#stripedPrimaryG06)' } }}
            />
            {/* 4. Resultados - striped secondary (teal with white stripes) */}
            <VictoryBar
              data={resultadosData}
              barWidth={12}
              style={{ data: { fill: 'url(#stripedSecondaryG06)' } }}
            />
          </VictoryStack>

          {/* Leverage Line - scaled to millones axis - NO labels */}
          <VictoryLine
            data={leverageLineData.map(d => ({
              x: d.x,
              y: d.y * scaleFactor
            }))}
            style={{
              data: {
                stroke: COLORS.primary,
                strokeWidth: 1.2
              }
            }}
          />

          {/* Leverage Points with small value labels */}
          <VictoryScatter
            data={leverageScatterData.map(d => ({
              ...d,
              y: d.y * scaleFactor
            }))}
            size={3}
            style={{ data: { fill: COLORS.primary } }}
            labelComponent={<VictoryLabel dy={-8} style={{ fontSize: 8, fill: COLORS.primary }} />}
          />
        </VictoryChart>
      </div>

      <p className="chart-source">
        Fuente: COAC / Elaboración: UnionRatings
      </p>
    </div>
  );
};

export default Grafico06;
